import { AgreementSignatureStatus } from "store/agreementsStore";
import type { IArticle } from "store/articleStore";
import type { TPortalType } from "store/types";

/**
 * True when a signed/sent PDF file is available for the article. Used to decide
 * between PDF viewer and HTML template preview, and to pick the download path.
 */
export const hasContractFile = (article: Pick<IArticle, "contractSigned"> | null | undefined): boolean =>
    article?.contractSigned != null && article.contractSigned > AgreementSignatureStatus.TO_SIGN;

/**
 * The contract tab is disabled for non-beneficiary portals only when no agreement
 * row exists AND no contract file is attached. A draft agreement (preview HTML)
 * or a manually uploaded signed contract (storageId "crealo:*", agreementStatus null)
 * both enable the tab.
 */
export const isContractTabDisabled = (
    portalType: TPortalType,
    article: Pick<IArticle, "agreementStatus" | "contractSigned"> | null | undefined,
): boolean => portalType !== "beneficiary" && !article?.agreementStatus && !hasContractFile(article);

/**
 * True when the beneficiary portal is viewing an article whose statement has
 * not yet been transmitted (lastSentAt is falsy).
 */
export const isStatementNotTransmitted = (
    portalType: TPortalType,
    article: Pick<IArticle, "lastSentAt"> | null | undefined,
): boolean => portalType === "beneficiary" && !article?.lastSentAt;

/**
 * Whether the contract tab should be rendered at all.
 * - Beneficiary portal: only when the contract is fully signed.
 * - All other portals: always show the tab.
 */
export const showContractTab = (
    portalType: TPortalType,
    article: Pick<IArticle, "contractSigned"> | null | undefined,
): boolean => {
    if (portalType === "beneficiary") {
        return article?.contractSigned === AgreementSignatureStatus.SIGNED;
    }
    return true;
};
